
import Ajv from 'ajv'
import { Plot } from '../models/Plot';
import { PlotSchema } from '../models/PlotSchema';
import { PlottingService } from './PlottingService';
import { destructureWithOmit, recursiveConvertSnakeToCamelCase } from '@/utils';
import { PlottingTypeGuardService } from './PlottingTypeGuardService';
import { IQueueExecutionRequest } from '@/shared/Executions/models/IQueueExecutionRequest';
import { CdnSchemaJsonParsingResult } from '@/shared/JsonEditorSchema/models/CdnSchemaJsonParsingResult';
import { SchemaJsonValidationService } from '@/shared/JsonEditorSchema/services/SchemaJsonValidationService';


export class PlottingJsonParsingResult extends CdnSchemaJsonParsingResult<Plot, PlottingJsonParsingInputType> {

    public name: string = "";

    constructor(
        valid: boolean,
        parsedValue?: any,
        errors: Array<string> = []
    ) {
        super(valid, parsedValue, errors);
        if (valid && parsedValue) {
            // Attempt to extract rules based on input shape
            if ('customPlots' in parsedValue && parsedValue.customPlots?.plots) {
                this.parsedEntities = parsedValue.customPlots.plots;
                this.name = parsedValue?.name;
                this.inputType = 'full';
            } else if ('plots' in parsedValue && Array.isArray(parsedValue.plots)) {
                this.parsedEntities = parsedValue.plots;
                this.name = parsedValue?.name;
                this.inputType = 'flat';
            } else if (Array.isArray(parsedValue)) {
                this.parsedEntities = parsedValue;
                this.inputType = 'array';
            }
        }
    }

    public withParsedPlots(plots: Plot[]): this {
        this.parsedEntities = plots;
        return this;
    }
}

// Represents a potential input json file structure with a plots Array property
interface IFlatPlotsArrayStructure {
    plots: Plot[];
}

// Represents a potential input json file structure that is solely an array of plot
type IPlotsArray = Plot[];


export type PlottingJsonParsingInputType = 'full' | 'flat' | 'array';

export class PlottingJsonParsingService extends SchemaJsonValidationService<Plot, PlottingJsonParsingInputType, PlotSchema> {

    static readonly injectableKey = 'PlottingJsonParsingService';

    get injectableKey() {
        return (this.constructor as typeof PlottingJsonParsingService).injectableKey;
    }

    //@ts-ignore
    public initializeValidators(defaultType?: string): void {
        //noop
    }

    private _fullPlotsPayloadStructureSchemaValidator = new Ajv().compile<IQueueExecutionRequest>({
        type: 'object',
        properties: {
            customPlots: {
                type: 'object',
                properties: {
                    plots: {
                        type: 'array',
                        items: {
                            type: 'object',
                            properties: {
                                type: { type: 'string' }
                            },
                            required: ['type'],
                            additionalProperties: true
                        }
                    }
                },
                required: ['plots'],
                additionalProperties: true
            }
        },
        required: ['customPlots'],
        additionalProperties: true
    });

    private _flatPlotsArrayStructureSchemaValidator = new Ajv().compile<IFlatPlotsArrayStructure>({
        type: 'object',
        properties: {
            plots: {
                type: 'array',
                items: {
                    type: 'object',
                    properties: {
                        type: { type: 'string' }
                    },
                    required: ['type'],
                    additionalProperties: true
                }
            }
        },
        required: ['plots'],
        additionalProperties: true
    });

    private _plotsArraySchemaValidator = new Ajv().compile<IPlotsArray>({
        type: 'array',
        items: {
            type: 'object',
            properties: {
                type: { type: 'string' }
            },
            required: ['type'],
            additionalProperties: true
        }
    });

    // Attempts to parse, validate, and initialize rules array that can operate with the UI
    // Returns validation result indicating parse and schema validation success, optionally on success
    // with UI-initializd rules array.
    public tryParseEntitiesFromJson(value: string, plotSchemas: Array<PlotSchema>): PlottingJsonParsingResult {
        if (plotSchemas == null || plotSchemas.length <= 0) throw new Error("plotSchemas is required");

        var result = new PlottingJsonParsingResult(true);

        if (value == null) {
            result.valid = false;
            result.errors.push("Cannot parse JSON from null or empty input value.");
            return result;
        }
        try {
            result.parsedValue = JSON.parse(value);
            recursiveConvertSnakeToCamelCase(result.parsedValue)
            return this.tryParsePlots(result, plotSchemas);
        }
        catch (error: any) {
            result.valid = false;
            result.errors.push(error);
        }

        return result;

    }

    private tryParsePlots(result: PlottingJsonParsingResult, plotSchemas: Array<PlotSchema>): PlottingJsonParsingResult {

        if (result.parsedValue == null) throw new Error("Invalid plots.parseValue encountered trying to validatePlotStructure.");

        const parsed = result.parsedValue;
        let plots: Plot[] | undefined;

        // reset errors from any prior validation
        this._fullPlotsPayloadStructureSchemaValidator.errors = null;
        this._flatPlotsArrayStructureSchemaValidator.errors = null;
        this._plotsArraySchemaValidator.errors = null;

        if (this._fullPlotsPayloadStructureSchemaValidator(parsed)) {
            plots = parsed.customPlots.plots;
        } else if (this._flatPlotsArrayStructureSchemaValidator(parsed)) {
            plots = parsed.plots;
        } else if (this._plotsArraySchemaValidator(parsed)) {
            plots = parsed;
        } else {
            const errors = [
                ...(this._fullPlotsPayloadStructureSchemaValidator.errors || []),
                ...(this._flatPlotsArrayStructureSchemaValidator.errors || []),
                ...(this._plotsArraySchemaValidator.errors || [])
            ]
                .map((e: any) => `${e.instancePath} ${e.message}`)
                .slice(0, 5);

            return new PlottingJsonParsingResult(false, undefined, errors);
        }

        try {
            const initialized = plots.map(x => this.initializeEntityWithConfiguration(x, plotSchemas));
            return new PlottingJsonParsingResult(true, parsed).withParsedPlots(initialized);
        } catch (err: any) {
            return new PlottingJsonParsingResult(false, parsed, [`${err.message}`]);
        }

    }

    // For lack of a better term, invert the flattening of a rule and instead populate its configuration
    // from the rule itself when loading rules into the UI
    public initializeEntityWithConfiguration(plot: Plot, plotSchemas: Array<PlotSchema>): Plot {
        if (plot == null) throw new Error("plot is required");
        if (!plot.type) throw new Error("plot.type is required")
        return this.initializeEntity(plot, plotSchemas, 1);
    }



    public initializeEntity(plot: Plot, plotSchemas: Array<PlotSchema>, configurationLevel: number): Plot {
        let initializedPlot = plot;
        //Match the incoming type on case-sensitive known rules
        const plotSchema = PlottingService.tryGetPlotSchemaByType(plot.type, plotSchemas);
        if (plotSchema != null) {
            initializedPlot = PlottingService.createPlotFromPlotSchema(plotSchema);
            if (plot.description != null) initializedPlot.description = plot.description;
            initializedPlot._type = plot.type;
            initializedPlot.configuration = destructureWithOmit(plot, []);
            initializedPlot.configuration._type = plot.type;

            // Special-case for rule comprised of rules (E.g. Data Grouping) or RuleSchema with payload_template that may have groups 
            const plotRequiresRecursiveInitialization = this.plotRequiresRecursiveInitialization(plot);
            //TODO: evaluate payload_template itself for groups instead of just disabling
            const plotSchemaRequiresRecursiveInitialization = this.plotSchemaRequiresRecursiveInitialization(plotSchema);

            if (configurationLevel > 1 &&
                (plotRequiresRecursiveInitialization || plotSchemaRequiresRecursiveInitialization)) {
                throw new Error("Non top-level plots that contain plots not supported.");
            }
            else if (plotRequiresRecursiveInitialization) {
                throw new Error("Plots containing child plots is not supported.");
                /*
                const initializedSubPlots = new Array<Plot>();
                (initializedPlot.configuration?.plots || []).forEach((p: Plot) => {
                    initializedSubPlots.push(this.initializePlot(p, plotSchemas, configurationLevel + 1))
                });
                initializedPlot.configuration.rules = initializedSubPlots;
                */
            }
        }
        return initializedPlot;
    }

    private plotRequiresRecursiveInitialization(plot: Plot): boolean {
        return PlottingTypeGuardService.supportsChildPlots(plot);
    }

    private plotSchemaRequiresRecursiveInitialization(plotSchema: PlotSchema): boolean {
        return PlottingTypeGuardService.supportsChildPlots(plotSchema);
    }



}